import axios from "axios";
import { localStorageService } from "./local-storage.service";
import { EagerdogErrors } from "@eagerdog/constants";
import { Config } from "@eagerdog/config";

// ---------------
let failedQueue: Array<{
  resolve: (token: string | null) => void;
  reject: (err: any) => void;
}> = [];

// ---------------
// Processes any outstanding requests in the failed queue.
const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

// ---------------
let isRefreshing = false;
let sessionEnded = false;

// Refresh uses cookies, not the Authorization header — keep it off the
// shared axios instance so a 401 here cannot re-enter the interceptor.
const refreshAxios = axios.create({
  withCredentials: true,
});

// Pages that are meant to work without a login. A leftover session from
// days ago on these routes should drop the stale token and stay put —
// not bounce a "View Event" email visitor to /login. A session that
// died while the user was still actively using the app still goes to
// login so they are not left looking like a logged-in non-admin.
const isPublicPath = (pathname: string): boolean => {
  if (
    pathname === "/" ||
    pathname === "/events" ||
    pathname === "/courses" ||
    pathname === "/kennel-clubs" ||
    pathname === "/privacy-policy" ||
    pathname === "/terms-of-service" ||
    pathname === "/premium"
  ) {
    return true;
  }
  if (pathname.startsWith("/embed/") || pathname.startsWith("/documents/") || pathname.startsWith("/dogs/")) {
    return true;
  }
  // /e/:handle and /e/:handle/summary — not manage / register / payment
  if (/^\/e\/[^/]+(\/summary)?$/.test(pathname)) {
    return true;
  }
  // /c/:handle — not register
  if (/^\/c\/[^/]+$/.test(pathname)) {
    return true;
  }
  // /k/:clubId and /k/:clubId/:tab — not manage
  if (/^\/k\/[^/]+(?!\/manage)(\/[^/]+)?$/.test(pathname) && !pathname.includes("/manage")) {
    return true;
  }
  return false;
};

const isAuthFlowPath = (pathname: string): boolean => {
  return (
    pathname === "/login" ||
    pathname === "/logout" ||
    pathname.startsWith("/register") ||
    pathname.startsWith("/verify-email") ||
    pathname === "/send-code" ||
    pathname === "/reset-password"
  );
};

const decodeJwtPayload = (token: string): { exp?: number } | null => {
  try {
    const part = token.split(".")[1];
    if (!part) {
      return null;
    }
    const base64 = part.replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64 + "=".repeat((4 - (base64.length % 4)) % 4);
    return JSON.parse(atob(padded));
  } catch {
    return null;
  }
};

const getStoredAccessToken = (): string | null => {
  return localStorageService.getItem(localStorageService.ACCESS_TOKEN);
};

// Treat the token as stale 30s before JWT expiry so we refresh before
// the next API call fails.
const isAccessTokenStale = (token: string, skewMs = 30_000): boolean => {
  const payload = decodeJwtPayload(token);
  if (!payload?.exp) {
    return true;
  }
  return Date.now() >= payload.exp * 1000 - skewMs;
};

// Access tokens last ~5 minutes; refresh tokens ~60. If the access
// token expired within the last two hours this was an in-use session
// that just died, not a leftover token from last week.
const RECENT_SESSION_MS = 2 * 60 * 60 * 1000;

const wasRecentlyAuthenticated = (): boolean => {
  const token = getStoredAccessToken();
  if (!token) {
    return false;
  }
  const payload = decodeJwtPayload(token);
  if (!payload?.exp) {
    return true;
  }
  const expiredAt = payload.exp * 1000;
  if (Date.now() < expiredAt) {
    return true;
  }
  return Date.now() - expiredAt < RECENT_SESSION_MS;
};

const clearLocalSession = () => {
  localStorageService.removeItem(localStorageService.ACCESS_TOKEN);
  localStorageService.removeItem(localStorageService.USER);
  localStorageService.removeItem(localStorageService.CAPACITIES);
  localStorageService.removeItem(localStorageService.CLUB);
};

const redirectToLogin = (pathname: string) => {
  const returnTo = `${pathname}${window.location.search || ""}`;
  window.location.href =
    `/logout?session_expired=1&r=${encodeURIComponent(returnTo)}`;
};

const handleRefreshFailure = () => {
  if (sessionEnded) {
    return;
  }
  sessionEnded = true;
  isRefreshing = false;

  const pathname = window.location.pathname || "/";
  if (isAuthFlowPath(pathname)) {
    clearLocalSession();
    return;
  }

  // Leftover token on a public page (View Event emails): drop it and
  // reload so the UI shows logged-out, not a zombie non-admin.
  if (isPublicPath(pathname) && !wasRecentlyAuthenticated()) {
    clearLocalSession();
    window.location.reload();
    return;
  }

  clearLocalSession();
  redirectToLogin(pathname);
};

const doRefresh = (): Promise<string> => {
  return refreshAxios
    .get(`${Config.web.REACT_APP_API_URL}/api/auth/refresh`)
    .then((result) => {
      const accessToken = result.data.access_token;
      if (!accessToken) {
        throw new Error("No access_token in refresh response");
      }
      localStorageService.setItem(localStorageService.ACCESS_TOKEN, accessToken);
      return accessToken;
    });
};

const refreshAccessToken = (): Promise<string> => {
  if (sessionEnded) {
    return Promise.reject(new Error("Session ended"));
  }

  if (isRefreshing) {
    return new Promise<string | null>((resolve, reject) => {
      failedQueue.push({ resolve, reject });
    }).then((token) => {
      if (!token) {
        throw new Error("Token refresh failed");
      }
      return token;
    });
  }

  isRefreshing = true;
  return doRefresh()
    .then((accessToken) => {
      isRefreshing = false;
      processQueue(null, accessToken);
      return accessToken;
    })
    .catch((err) => {
      processQueue(err, null);
      handleRefreshFailure();
      throw err;
    });
};

const ensureFreshSession = () => {
  if (typeof window === "undefined" || sessionEnded) {
    return;
  }
  const pathname = window.location.pathname || "/";
  if (isAuthFlowPath(pathname)) {
    return;
  }
  const token = getStoredAccessToken();
  if (!token || !isAccessTokenStale(token)) {
    return;
  }
  refreshAccessToken().catch(() => {
    // handleRefreshFailure already ran
  });
};

if (typeof window !== "undefined") {
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") {
      ensureFreshSession();
    }
  });
  window.addEventListener("focus", () => {
    ensureFreshSession();
  });
  window.addEventListener("pageshow", (event) => {
    if (event.persisted) {
      ensureFreshSession();
    }
  });
  ensureFreshSession();
}

// -----------------
axios.interceptors.request.use(
  (config) => {
    const token = getStoredAccessToken();

    if (token) {
      config.headers!["Authorization"] = "Bearer " + token;
    }

    // needed for cookies to be sent back
    config.withCredentials = true;

    return config;
  },

  (error) => {
    return Promise.reject(error);
  }
);

// ---------------
axios.interceptors.response.use(
  (response) => {
    return response;
  },

  async (error) => {
    const originalRequest = error.config;

    // Every *permission/business* 401 in the API carries its own specific
    // eagerdog_error_code (e.g. NO_OBJECT_PERMISSIONS, invalid login
    // credentials, etc). A bare 401 with no code only comes from AccessGuard
    // falling back to a plain UnauthorizedException — i.e. the request's
    // access token was missing, malformed, or otherwise couldn't be matched
    // to a logged-in user (not necessarily because it literally expired).
    // Treat that the same as ACCESS_TOKEN_EXPIRED: try a silent refresh
    // before giving up, instead of surfacing a raw "Unauthorized" error.
    const isBareUnauthorized =
      error.response?.status === 401 && !error.response?.data?.eagerdog_error_code;

    // If there's no access token in localStorage at all, this browser was
    // never logged in (or was already logged out) - a bare 401 here is
    // expected/normal (e.g. an auth-optional call made from a public page
    // by an anonymous visitor), not a sign that a session "expired". There
    // is nothing to refresh and no session worth force-clearing, so let it
    // fail quietly instead of bouncing the visitor to /logout -> /login.
    const hasLocalSession = !!getStoredAccessToken();

    if (
      hasLocalSession &&
      originalRequest &&
      (error.response?.data?.eagerdog_error_code ===
        EagerdogErrors.AUTH.ACCESS_TOKEN_EXPIRED ||
        isBareUnauthorized)
    ) {
      if (originalRequest._retried) {
        return Promise.reject(error);
      }

      originalRequest._retried = true;
      return refreshAccessToken()
        .then((token) => {
          originalRequest.headers = originalRequest.headers || {};
          originalRequest.headers["Authorization"] = "Bearer " + token;
          return axios(originalRequest);
        })
        .catch((err) => {
          return Promise.reject(err);
        });
    }

    return Promise.reject(error);
  }
);

// ---------------
export namespace httpService {
  export const get = axios.get;
  export const post = axios.post;
  export const put = axios.put;
  export const patch = axios.patch;
  export const delete_ = axios.delete;
}
